import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useStore } from '../store/store'
import { computeIndicators, contractEv, crossedTarget, lastNonNull, noAskCents } from '../lib/math'
import type { Contract, ContractSide, ContractWatch } from '../domain/types'
import { opportunities } from '../store/selectors'
import { fmtCents, fmtCurrency, fmtPct, fmtSigned } from '../lib/format'
import { Card, EmptyState, PageHead, Pill, type Tone } from '../components/ui'
import { Sparkline } from '../components/charts'
import { OpportunityCard } from '../components/widgets'

const trendTone: Record<string, Tone> = { uptrend: 'green', downtrend: 'red', sideways: 'gray' }

export function Watchlist() {
  const store = useStore()
  const watchedAssets = store.assets.filter((a) => store.watchlistAssetIds.includes(a.id))
  const contractWatches = materializeWatches(
    store.markets.flatMap((market) => market.contracts),
    store.contractWatches ?? [],
    store.watchlistContractIds,
  )
  const watchedIds = new Set([...store.watchlistContractIds, ...contractWatches.map((w) => w.contractId)])
  const watchedContracts = store.markets.flatMap((m) =>
    m.contracts.filter((c) => watchedIds.has(c.id)).map((c) => ({ market: m.title, c, watch: contractWatches.find((w) => w.contractId === c.id) })),
  )
  const best = useMemo(() => opportunities(store).filter((o) => !o.noTrade.skip).slice(0, 4), [store])
  const upsertWatch = store.upsertContractWatch

  return (
    <div className="stack">
      <PageHead title="Watchlist">
        The assets and contracts you're tracking, plus the strongest edges among them. Watching is free; entering should not be.
      </PageHead>

      <Card title="Stocks">
        {watchedAssets.length === 0 ? (
          <EmptyState>No watched assets. Add some from the <Link to="/stocks">Stock Analyzer</Link>.</EmptyState>
        ) : (
          <div className="grid cols-3">
            {watchedAssets.map((a) => {
              const ind = computeIndicators(a.candles)
              const last = a.candles[a.candles.length - 1]
              const prev = a.candles[a.candles.length - 2] ?? last
              const change = prev.close > 0 ? (last.close - prev.close) / prev.close : 0
              const rsiVal = lastNonNull(ind.rsi14)
              return (
                <div key={a.id} className="card tight">
                  <div className="row between">
                    <strong>{a.symbol}</strong>
                    <Pill tone={trendTone[ind.trend.trend]}>{ind.trend.trend}</Pill>
                  </div>
                  <Sparkline values={a.candles.slice(-60).map((c) => c.close)} color="auto" height={44} />
                  <div className="row between" style={{ marginTop: 6 }}>
                    <span className="mono">{fmtCurrency(last.close)}</span>
                    <span className={`mono ${change >= 0 ? 'pos' : 'neg'}`}>{fmtSigned(change * 100, 2)}%</span>
                  </div>
                  <div className="muted" style={{ fontSize: 11 }}>RSI {rsiVal ? rsiVal.toFixed(0) : '—'}</div>
                </div>
              )
            })}
          </div>
        )}
      </Card>

      <Card title="Prediction contracts">
        {watchedContracts.length === 0 ? (
          <EmptyState>No watched contracts. Star some on the <Link to="/markets">Prediction Markets</Link> page.</EmptyState>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Market</th><th>Contract</th><th>Side</th><th>Current ask</th><th>Target</th><th>Your prob</th><th>Fee-adjusted EV/$</th><th>Status</th></tr></thead>
              <tbody>
                {watchedContracts.map(({ market, c, watch }) => {
                  const side = watch?.side ?? 'yes'
                  const currentAsk = side === 'yes' ? c.askCents : noAskCents(c.bidCents)
                  const ev = c.modelProb !== undefined
                    ? contractEv({ side, modelProb: c.modelProb, yesBidCents: c.bidCents, yesAskCents: c.askCents })
                    : null
                  const target = watch?.targetPriceCents ?? currentAsk
                  const hit = crossedTarget(currentAsk, target)
                  return (
                    <tr key={c.id}>
                      <td className="faint">{market}</td>
                      <td>{c.title}</td>
                      <td>
                        <select
                          value={side}
                          onChange={(e) => upsertWatch({ contractId: c.id, side: e.target.value as ContractSide, targetPriceCents: target, note: watch?.note })}
                          style={{ width: 70 }}
                        >
                          <option value="yes">YES</option>
                          <option value="no">NO</option>
                        </select>
                      </td>
                      <td className="mono">{fmtCents(currentAsk)}</td>
                      <td style={{ width: 96 }}>
                        <input
                          className="input mono"
                          type="number"
                          min={1}
                          max={99}
                          value={target}
                          onChange={(e) => upsertWatch({ contractId: c.id, side, targetPriceCents: Number(e.target.value), note: watch?.note })}
                        />
                      </td>
                      <td className="mono">{c.modelProb !== undefined ? fmtPct(c.modelProb, 0) : '—'}</td>
                      <td className={`mono ${ev ? (ev.evPerDollar >= 0 ? 'pos' : 'neg') : 'muted'}`}>{ev ? fmtSigned(ev.evPerDollar, 3) : '—'}</td>
                      <td>{hit ? <Pill tone="green">target hit</Pill> : <Pill tone="gray">waiting</Pill>}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="Best edges in your watchlist" action={<Link to="/scanner" className="btn sm">Full scanner</Link>}>
        {best.length === 0 ? (
          <EmptyState>No positive-edge ideas right now.</EmptyState>
        ) : (
          <div className="grid cols-2">
            {best.map((o) => <OpportunityCard key={o.input.id} opp={o} />)}
          </div>
        )}
      </Card>
    </div>
  )
}

function materializeWatches(contracts: Contract[], watches: ContractWatch[], watchIds: string[]): ContractWatch[] {
  const out = [...watches]
  const existingIds = new Set(out.map((watch) => watch.contractId))
  for (const id of watchIds) {
    if (existingIds.has(id)) continue
    const contract = contracts.find((candidate) => candidate.id === id)
    if (!contract) continue
    out.push({
      contractId: id,
      side: 'yes',
      targetPriceCents: contract.askCents,
      addedAt: '',
      baselineYesCents: contract.lastCents,
    })
  }
  return out
}
